import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateFormattingService {
  private readonly locale = 'de-DE';
  private readonly nbsp = '\u00A0';

  /**
   * Formats a date in short format: "Mi., 16. Jul 2025"
   */
  formatDateShort(date: Date): string {
    return date.toLocaleDateString(this.locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Formats a date with time in short format: "Mi., 16. Jul 2025, 11:30"
   */
  formatDateTimeShort(date: Date): string {
    return date.toLocaleDateString(this.locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Formats time only: "11:30"
   */
  formatTimeOnly(date: Date): string {
    return date.toLocaleDateString(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Formats date without weekday: "16. Jul 2025"
   */
  formatDateNoWeekday(date: Date): string {
    return date.toLocaleDateString(this.locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Formats a date range: "16. - 18. Jul 2025"
   */
  formatDateRange(startDate: Date, endDate: Date): string {
    const startFormatted = startDate.toLocaleDateString(this.locale, {
      day: 'numeric',
    });
    const endFormatted = endDate.toLocaleDateString(this.locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return `${startFormatted}. - ${endFormatted}`;
  }

  /**
   * Formats event date for cards: "Mi., 16. Jul 2025, 11:30" or "Mi., 16. Jul 2025, 11:30 - 13:00"
   */
  formatEventDate(startDate: Date, endDate?: Date): string {
    const startFormatted = this.formatDateTimeShort(startDate);

    if (!endDate || startDate.toDateString() === endDate.toDateString()) {
      // Same day or no end date
      if (endDate) {
        const endTime = this.formatTimeOnly(endDate);
        return `${startFormatted} - ${endTime}`;
      }
      return startFormatted;
    } else {
      // Different days
      const endFormatted = this.formatDateTimeShort(endDate);
      return `${startFormatted} - ${endFormatted}`;
    }
  }

  /**
   * Formats year only: "2025"
   */
  formatYearOnly(date: Date): string {
    return date.getFullYear().toString();
  }

  /**
   * Formats month name only: "Juli"
   */
  formatMonthName(date: Date): string {
    return date.toLocaleString(this.locale, { month: 'long' });
  }

  /**
   * Formats date with time using the same format as the existing event-date pipe
   */
  formatEventDateTime(startDate: Date, endDate?: Date): string {
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };

    const formattedStartDate = startDate.toLocaleDateString(this.locale, formatOptions);

    if (!endDate || startDate.toDateString() === endDate.toDateString()) {
      return formattedStartDate;
    } else {
      const formattedEndDate = endDate.toLocaleDateString(this.locale, formatOptions);
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  }

  /**
   * Formats event date in compact format: "22.07, 18:00" or "22.07, 18:00 - 31.07.25, 14:00"
   */
  formatEventDateCompact(startDate: Date, endDate?: Date): string {
    const formatDateCompact = (date: Date, includeYear: boolean = false): string => {
      const weekday = date.toLocaleDateString(this.locale, { weekday: 'short' });
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = includeYear ? date.getFullYear().toString().slice(-2) : '';
      return `${weekday}.,${this.nbsp}${day}.${month}.${year}`;
    };

    const formatTimeCompact = (date: Date): string => {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const startDateFormatted = formatDateCompact(startDate);
    const startTimeFormatted = formatTimeCompact(startDate);

    if (!endDate || startDate.toDateString() === endDate.toDateString()) {
      // Same day or no end date
      if (endDate) {
        const endTimeFormatted = formatTimeCompact(endDate);
        return `${startDateFormatted},${this.nbsp}${startTimeFormatted} - ${endTimeFormatted}`;
      }
      return `${startDateFormatted},${this.nbsp}${startTimeFormatted}`;
    } else {
      // Different days - include year in end date
      const endDateFormatted = formatDateCompact(endDate, true);
      const endTimeFormatted = formatTimeCompact(endDate);
      return `${startDateFormatted},${this.nbsp}${startTimeFormatted} - ${endDateFormatted},${this.nbsp}${endTimeFormatted}`;
    }
  }

  /**
   * Formats date in compact format: "22.07" or "22.07.25"
   */
  formatDateCompact(date: Date, includeYear: boolean = false): string {
    const weekday = date.toLocaleDateString(this.locale, { weekday: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = includeYear ? `.${date.getFullYear().toString().slice(-2)}` : '';
    return `${weekday},${this.nbsp}${day}.${month}${year}`;
  }
}
